import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PostmanUI.scss';

const PostmanUI = ({ endpoints, apiKey, mode }) => {
  const [selectedEndpoint, setSelectedEndpoint] = useState(endpoints[0]);
  const [requestBody, setRequestBody] = useState('');
  const [queryParams, setQueryParams] = useState([]);
  const [pathParams, setPathParams] = useState({});
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('body'); // body, params, headers

  const baseUrl = process.env.REACT_APP_API_BASE_URL.replace('api/v1', 'external/v1');

  useEffect(() => {
    // Initialize request body and params when endpoint changes
    if (selectedEndpoint) {
      if (selectedEndpoint.request?.body) {
        setRequestBody(JSON.stringify(selectedEndpoint.request.body, null, 2));
      } else {
        setRequestBody('');
      }

      // Initialize query params
      if (selectedEndpoint.request?.queryParameters) {
        const params = Object.entries(selectedEndpoint.request.queryParameters).map(([key, value]) => ({
          key,
          value,
          enabled: true
        }));
        setQueryParams(params);
      } else {
        setQueryParams([]);
      }

      // Initialize path params
      if (selectedEndpoint.request?.pathParameters) {
        setPathParams(selectedEndpoint.request.pathParameters);
      } else {
        setPathParams({});
      }

      // Clear previous response
      setResponse(null);
      setError(null);
    }
  }, [selectedEndpoint]);

  const addQueryParam = () => {
    setQueryParams([...queryParams, { key: '', value: '', enabled: true }]);
  };

  const updateQueryParam = (index, field, value) => {
    const updated = [...queryParams];
    updated[index][field] = value;
    setQueryParams(updated);
  };

  const removeQueryParam = (index) => {
    setQueryParams(queryParams.filter((_, i) => i !== index));
  };

  const updatePathParam = (key, value) => {
    setPathParams({ ...pathParams, [key]: value });
  };

  const buildUrl = () => {
    let url = baseUrl + selectedEndpoint.path;

    // Replace path parameters
    Object.entries(pathParams).forEach(([key, value]) => {
      url = url.replace(`{${key}}`, value);
    });

    // Add query parameters
    const enabledParams = queryParams.filter(p => p.enabled && p.key);
    if (enabledParams.length > 0) {
      const queryString = enabledParams.map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`).join('&');
      url += '?' + queryString;
    }

    return url;
  };

  const sendRequest = async () => {
    if (!apiKey) {
      setError('API key is required. Please generate an API key first.');
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const url = buildUrl();
      const headers = {
        'x-api-key': apiKey,
        'Content-Type': 'application/json'
      };

      let data = null;
      if (selectedEndpoint.method !== 'GET' && requestBody.trim()) {
        try {
          data = JSON.parse(requestBody);
        } catch (e) {
          setError('Invalid JSON in request body');
          setLoading(false);
          return;
        }
      }

      const startTime = Date.now();
      const result = await axios({
        method: selectedEndpoint.method.toLowerCase(),
        url,
        headers,
        data
      });
      const endTime = Date.now();

      setResponse({
        status: result.status,
        statusText: result.statusText,
        headers: result.headers,
        data: result.data,
        time: endTime - startTime
      });
    } catch (err) {
      setError(err.response ? `${err.response.status} ${err.response.statusText}` : err.message);
      if (err.response) {
        setResponse({
          status: err.response.status,
          statusText: err.response.statusText,
          headers: err.response.headers,
          data: err.response.data,
          error: true
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getMethodClass = (method) => {
    return method.toLowerCase();
  };

  const hasPathParams = selectedEndpoint?.path?.includes('{');

  return (
    <div className="postman-ui">
      <div className="postman-sidebar">
        <div className="sidebar-header">
          <h4>Endpoints</h4>
        </div>
        <div className="endpoint-list">
          {endpoints.map((endpoint) => (
            <div
              key={endpoint.id}
              className={`endpoint-item ${selectedEndpoint?.id === endpoint.id ? 'active' : ''}`}
              onClick={() => setSelectedEndpoint(endpoint)}
            >
              <span className={`method-badge ${getMethodClass(endpoint.method)}`}>
                {endpoint.method}
              </span>
              <span className="endpoint-name">{endpoint.title}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="postman-content">
        <div className="request-section">
          <div className="request-header">
            <span className={`method-badge ${getMethodClass(selectedEndpoint?.method)}`}>
              {selectedEndpoint?.method}
            </span>
            <div className="url-input">{buildUrl()}</div>
            <button
              className="send-button"
              onClick={sendRequest}
              disabled={loading || !apiKey}
            >
              {loading ? 'Sending...' : 'Send'}
            </button>
          </div>

          {selectedEndpoint?.description && (
            <div className="endpoint-description">
              <i className="mdi mdi-information-outline"></i>
              <span>{selectedEndpoint.description}</span>
            </div>
          )}

          <div className="request-tabs">
            {hasPathParams && (
              <button
                className={`tab-button ${activeTab === 'path' ? 'active' : ''}`}
                onClick={() => setActiveTab('path')}
              >
                Path Params
              </button>
            )}
            <button
              className={`tab-button ${activeTab === 'params' ? 'active' : ''}`}
              onClick={() => setActiveTab('params')}
            >
              Query Params
            </button>
            {selectedEndpoint?.method !== 'GET' && (
              <button
                className={`tab-button ${activeTab === 'body' ? 'active' : ''}`}
                onClick={() => setActiveTab('body')}
              >
                Body
              </button>
            )}
            <button
              className={`tab-button ${activeTab === 'headers' ? 'active' : ''}`}
              onClick={() => setActiveTab('headers')}
            >
              Headers
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'path' && hasPathParams && (
              <div className="path-params">
                {selectedEndpoint.path.match(/\{([^}]+)\}/g)?.map(match => {
                  const key = match.slice(1, -1);
                  return (
                    <div key={key} className="param-row">
                      <input
                        type="text"
                        value={key}
                        disabled
                        className="param-key"
                        placeholder="Key"
                      />
                      <input
                        type="text"
                        value={pathParams[key] || ''}
                        onChange={(e) => updatePathParam(key, e.target.value)}
                        className="param-value"
                        placeholder="Value"
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {activeTab === 'params' && (
              <div className="query-params">
                {queryParams.map((param, index) => (
                  <div key={index} className="param-row">
                    <input
                      type="checkbox"
                      checked={param.enabled}
                      onChange={(e) => updateQueryParam(index, 'enabled', e.target.checked)}
                      className="param-checkbox"
                    />
                    <input
                      type="text"
                      value={param.key}
                      onChange={(e) => updateQueryParam(index, 'key', e.target.value)}
                      className="param-key"
                      placeholder="Key"
                    />
                    <input
                      type="text"
                      value={param.value}
                      onChange={(e) => updateQueryParam(index, 'value', e.target.value)}
                      className="param-value"
                      placeholder="Value"
                    />
                    <button
                      className="remove-param"
                      onClick={() => removeQueryParam(index)}
                    >
                      <i className="mdi mdi-close"></i>
                    </button>
                  </div>
                ))}
                <button className="add-param-button" onClick={addQueryParam}>
                  + Add Parameter
                </button>
              </div>
            )}

            {activeTab === 'body' && selectedEndpoint?.method !== 'GET' && (
              <div className="request-body">
                <textarea
                  value={requestBody}
                  onChange={(e) => setRequestBody(e.target.value)}
                  placeholder="Enter JSON body"
                  className="body-textarea"
                />
              </div>
            )}

            {activeTab === 'headers' && (
              <div className="headers-display">
                <div className="header-row">
                  <span className="header-key">x-api-key</span>
                  <span className="header-value">{apiKey || '(Not set)'}</span>
                </div>
                <div className="header-row">
                  <span className="header-key">Content-Type</span>
                  <span className="header-value">application/json</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="response-section">
          <div className="response-header">
            <h4>Response</h4>
            {response && (
              <div className="response-info">
                <span className={`status-badge ${response.error ? 'error' : 'success'}`}>
                  {response.status} {response.statusText}
                </span>
                {response.time && <span className="response-time">{response.time}ms</span>}
              </div>
            )}
          </div>

          {error && !response && (
            <div className="error-message">
              <i className="mdi mdi-alert-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {response && (
            <div className="response-body">
              <pre>
                <code>{JSON.stringify(response.data, null, 2)}</code>
              </pre>
            </div>
          )}

          {!response && !error && (
            <div className="no-response">
              <i className="mdi mdi-send"></i>
              <p>Send a request to see the response here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostmanUI;


